import EnhanceIcon from "assets/icons/EnhanceIcon";

import classNames from "classnames";
import {decodeCharacterReferences, truncateSentenceEnd} from "helpers/string";

import useEventMonitor from "hooks/mapx/useEventMonitor";
import useUserBehaviorTrackHook from "hooks/useUserBehaviorTrackHook";

import {ButtonsRow, CompanyInformation, CompanyStatistics, GeneralButton} from "mapx-components";
import React, {useCallback} from "react";
import {companySpecialityTagsSelector} from "store/filter/filterSelectors";
import {
	addCompanyToTargetList,
	removeCompanyFromTargetList,
} from "store/target-list/targetListAsyncActions";
import {
	isCompanyBeingAddedToTargetListSelector,
	isCompanyBeingRemovedFromTargetListSelector,
	isCompanySavedSelector,
	targetListIDSelector,
} from "store/target-list/targetListSelectors";
import css from "./companyCard.module.scss";
import {useAppDispatch, useAppSelector} from "hooks";
import {setSpecialityTagsForCompany} from "store/filter/companyAdvancedFiltersAsyncActions";
import {TCompanyCardProps, TCompanyCardTrackEvents} from "mapx-components/Cards/CompanyCard/types";
import {STSpecialty} from "api/filterOptionsApi/SpecialityApi/types";

export default function CompanyCard({
	company,
	isRecommended = false,
	position = 1,
	type = "listing",
	shouldTrack = true,
}: TCompanyCardProps) {
	const dispatch = useAppDispatch();

	const targetListID = useAppSelector(targetListIDSelector);

	const {trackStart, trackComplete, itemEventDetails, setEvent, setTargetListId} =
		useUserBehaviorTrackHook({
			itemType: "company",
			itemId: company.id,
			source: type,
			isRecommended,
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			position,
		});

	const containerRef = useEventMonitor({shouldTrack, itemEventDetails});

	const specialties = useAppSelector(companySpecialityTagsSelector);

	const isSaved = useAppSelector((state) => isCompanySavedSelector(state, company.id));

	const isCompanyBeingAdded = useAppSelector((state) =>
		isCompanyBeingAddedToTargetListSelector(state, company.id),
	);

	const isCompanyBeingRemoved = useAppSelector((state) =>
		isCompanyBeingRemovedFromTargetListSelector(state, company.id),
	);

	const loadingSave = isCompanyBeingAdded || isCompanyBeingRemoved;

	const trackEventReport = useCallback(
		(type: TCompanyCardTrackEvents) => {
			if (shouldTrack) {
				setTargetListId(targetListID);
				setEvent(type);
			}
		},
		[setEvent, setTargetListId, shouldTrack, targetListID],
	);

	const handleSaveItem = useCallback(() => {
		if (isSaved) {
			dispatch(removeCompanyFromTargetList(company.id));

			trackEventReport("remove");
		} else {
			dispatch(addCompanyToTargetList(company.id));

			trackEventReport("add");
		}
	}, [company.id, dispatch, isSaved, trackEventReport]);

	const handleSelectedSpecialities = useCallback(
		(item: STSpecialty) => {
			dispatch(setSpecialityTagsForCompany({id: String(item.id)}));
			trackEventReport("click");
		},
		[dispatch, trackEventReport],
	);

	return (
		<div
			data-testid={"companyCard"}
			onMouseEnter={trackStart}
			onMouseLeave={trackComplete}
			ref={containerRef}
		>
			<div className={classNames(css.company, {[css.recommended]: isRecommended})}>
				{isRecommended && (
					<span className={css.enhanceIcon}>
						<EnhanceIcon />
					</span>
				)}

				<div className={css.topRow}>
					<CompanyInformation
						company={company}
						from={type}
						isRecommended={isRecommended}
						position={position}
					/>
					<CompanyStatistics size={company?.size || 0} revenue={company?.revenue || 0} />
				</div>

				{company.description != null ? (
					<div className={css.infoCategory}>
						<span className={classNames(css.infoCompanyDescription, {})}>
							{decodeCharacterReferences(
								truncateSentenceEnd(company.description, 171, 173),
							)}
						</span>
					</div>
				) : (
					<div className={css.infoCategory} style={{minHeight: 20}} />
				)}

				<div className={css.bottomRow}>
					{company.specialties && company.specialties?.length > 0 ? (
						<ButtonsRow
							selectedItems={specialties}
							items={company.specialties}
							handleSelectedItem={handleSelectedSpecialities}
							trackEventReport={trackEventReport}
						/>
					) : (
						<span />
					)}

					<div className={css.rightContainer}>
						<GeneralButton
							isClicked={isSaved}
							handleClick={handleSaveItem}
							loadingSave={loadingSave}
							title={type === "project" ? "Remove" : isSaved ? "Saved" : "Save"}
							customClass={css.buttonWidth}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
